import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  ConfigProvider,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AssociatedBarnaRowFields,
  type AssociatedBarnaFormSlice,
  type AssociatedRowForm,
} from '../components/AssociatedBarnaRowFields';
import type { FormInstance } from 'antd/es/form';
import { EMPTY_BHED_FILTERS } from '../utils/bhedAttributeFilters';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  buildPratyaharPatch,
  createPratyahar,
  deletePratyahar,
  getPratyahar,
  listPratyahar,
  updatePratyahar,
  associatedBarnaLabel,
  associatedBarnasToBody,
  type AssociatedBarna,
  type Pratyahar,
} from '../api/pratyaharApi';
import { listBarna, type Barna } from '../api/barnaApi';
import { listBhed, type Bhed } from '../api/bhedApi';
import './PratyaharPage.css';

type PratyaharFormValues = {
  name: string;
  synonyms: string[];
  associated_barnas: AssociatedRowForm[];
};

function errMessage(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const msg = ax?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('; ');
  if (typeof msg === 'string') return msg;
  return ax?.message || fallback;
}

function formToPratyahar(id: number, v: PratyaharFormValues): Pratyahar {
  const associatedBarnas: AssociatedBarna[] = (v.associated_barnas ?? [])
    .map((row) => {
      const barnaId = row.barna_id == null ? null : Number(row.barna_id);
      if (barnaId == null || Number.isNaN(barnaId)) return null;
      const ids = (row.included_bhedas ?? []).map(Number).filter((n) => Number.isFinite(n));
      return { barnaId, includedBhedas: ids };
    })
    .filter((x): x is AssociatedBarna => x != null);
  return {
    id,
    name: v.name.trim(),
    synonyms: (v.synonyms ?? []).map((s) => String(s)),
    associatedBarnas,
  };
}

function pratyaharToFormValues(p: Pratyahar): PratyaharFormValues {
  return {
    name: p.name,
    synonyms: p.synonyms.length > 0 ? [...p.synonyms] : [],
    associated_barnas:
      p.associatedBarnas.length > 0
        ? p.associatedBarnas.map((a) => ({
            barna_id: a.barnaId,
            included_bhedas: [...a.includedBhedas],
            bhed_filters: { ...EMPTY_BHED_FILTERS },
          }))
        : [],
  };
}

export function PratyaharPage() {
  const [rows, setRows] = useState<Pratyahar[]>([]);
  const [barnaRows, setBarnaRows] = useState<Barna[]>([]);
  const [bhedRows, setBhedRows] = useState<Bhed[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Pratyahar | null>(null);
  const [saving, setSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<Pratyahar | null>(null);
  const [form] = Form.useForm<PratyaharFormValues>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pratyahars, barnas, bheds] = await Promise.all([listPratyahar(), listBarna(), listBhed()]);
      setRows(pratyahars);
      setBarnaRows(barnas);
      setBhedRows(bheds);
    } catch (err: unknown) {
      message.error(errMessage(err, 'Failed to load pratyahar'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const barnaLabel = useCallback(
    (id: number, assoc?: AssociatedBarna) => associatedBarnaLabel(id, assoc, barnaRows),
    [barnaRows]
  );

  const bhedLabel = useCallback(
    (id: number) => {
      const h = bhedRows.find((x) => x.id === id);
      return h ? `${h.text} (#${id})` : `#${id}`;
    },
    [bhedRows]
  );

  const associatedBarnaOptions = useMemo(() => {
    const toOption = (b: Barna) => ({ value: b.id, label: `${b.name} (#${b.id})` });
    const sortByName = (a: Barna, b: Barna) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    const swar = [...barnaRows].filter((b) => b.type === 'swar').sort(sortByName).map(toOption);
    const byanjan = [...barnaRows].filter((b) => b.type === 'byanjan').sort(sortByName).map(toOption);
    return [
      ...(swar.length > 0 ? [{ label: 'Swar', options: swar }] : []),
      ...(byanjan.length > 0 ? [{ label: 'Byanjan', options: byanjan }] : []),
    ];
  }, [barnaRows]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      name: '',
      synonyms: [],
      associated_barnas: [],
    });
    setModalOpen(true);
  };

  const openEdit = async (record: Pratyahar) => {
    try {
      const full = await getPratyahar(record.id);
      setEditing(full);
      form.setFieldsValue(pratyaharToFormValues(full));
      setModalOpen(true);
    } catch (err: unknown) {
      message.error(errMessage(err, 'Failed to load pratyahar'));
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const normalizeAssociatedForSubmit = (model: Pratyahar): Pratyahar => ({
    ...model,
    associatedBarnas: model.associatedBarnas.map((assoc) => {
      const barna = barnaRows.find((b) => b.id === assoc.barnaId);
      if (barna?.type === 'byanjan') return { ...assoc, includedBhedas: [] };
      return assoc;
    }),
  });

  const validateBhedBelongToBarna = (model: Pratyahar): boolean => {
    for (const assoc of model.associatedBarnas) {
      const barna = barnaRows.find((b) => b.id === assoc.barnaId);
      if (barna?.type === 'byanjan') {
        if (assoc.includedBhedas.length > 0) {
          message.error(`Byanjan barna ${barnaLabel(assoc.barnaId)} cannot include bhedas.`);
          return false;
        }
        continue;
      }
      if (barna && barna.type !== 'swar') {
        message.error(
          `Associated barna ${barnaLabel(assoc.barnaId)} must be type swar or byanjan. Change it or remove this row.`
        );
        return false;
      }
      const allowed = new Set(bhedRows.filter((h) => h.barnaId === assoc.barnaId).map((h) => h.id));
      for (const bid of assoc.includedBhedas) {
        if (!allowed.has(bid)) {
          message.error(
            `Bhed #${bid} is not part of barna ${barnaLabel(assoc.barnaId)}. Remove it or pick another barna.`
          );
          return false;
        }
      }
    }
    return true;
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const trimmedSynonyms = (values.synonyms ?? []).map((s) => String(s).trim()).filter((s) => s !== '');
      const nextModel = normalizeAssociatedForSubmit(
        formToPratyahar(editing?.id ?? 0, { ...values, synonyms: trimmedSynonyms })
      );
      if (!validateBhedBelongToBarna(nextModel)) return;

      if (!nextModel.name) {
        message.error('Name is required');
        return;
      }

      setSaving(true);
      if (editing) {
        const patch = buildPratyaharPatch(editing, { ...nextModel, id: editing.id });
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updatePratyahar(editing.id, patch);
        message.success('Pratyahar updated');
      } else {
        await createPratyahar({
          name: nextModel.name,
          synonyms: nextModel.synonyms,
          associated_barnas: associatedBarnasToBody(nextModel.associatedBarnas),
        });
        message.success('Pratyahar created');
      }
      closeModal();
      await load();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id: number) => {
    setDetailLoading(true);
    setDetailOpen(true);
    setDetail(null);
    try {
      setDetail(await getPratyahar(id));
    } catch (err: unknown) {
      message.error(errMessage(err, 'Failed to load pratyahar'));
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deletePratyahar(id);
      message.success('Deleted');
      await load();
    } catch (err: unknown) {
      message.error(errMessage(err, 'Delete failed'));
    }
  };

  const columns: ColumnsType<Pratyahar> = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    { title: 'Name', dataIndex: 'name', ellipsis: true },
    {
      title: 'Synonyms',
      dataIndex: 'synonyms',
      ellipsis: true,
      render: (syn: string[]) =>
        (syn ?? []).length > 0 ? (
          <Space size={[4, 4]} wrap>
            {syn.map((s) => (
              <Tag key={s}>{s}</Tag>
            ))}
          </Space>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Barna links',
      key: 'assoc',
      width: 120,
      render: (_, r) => r.associatedBarnas?.length ?? 0,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => void openDetail(record.id)}>
            View
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => void openEdit(record)}>
            Edit
          </Button>
          <Popconfirm title="Delete this pratyahar?" onConfirm={() => void handleDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <ConfigProvider theme={{ token: { fontFamily: "'Knswami', sans-serif" } }}>
      <div className="pratyahar-page-root" style={{ width: '100%' }}>
        <Card
          title="Pratyahar"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add pratyahar
            </Button>
          }
        >
      <Table<Pratyahar>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 900 }}
      />

      <Modal
        title={editing ? 'Edit pratyahar' : 'Add pratyahar'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void onSubmit()}
        confirmLoading={saving}
        width={720}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Each associated barna row links a <strong>swar</strong> or <strong>byanjan</strong> barna. For swar, use the
          attribute filters (hraswa, dirgha, pluta, anunasik, udatta, etc.) to narrow which <strong>bhed</strong> variants
          are selected — each group (matra, anunasik, swor) must match when checked; with no filters, all bheds are included. For byanjan, included bhedas are
          always empty. On update, sending associated rows replaces all of them.
        </Typography.Paragraph>
        <Form layout="vertical" form={form}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="e.g. इक्" />
          </Form.Item>

          <Typography.Text strong>Synonyms</Typography.Text>
          <Form.List name="synonyms">
            {(fields, { add, remove }) => (
              <div style={{ marginTop: 8 }}>
                {fields.map((field) => (
                  <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item {...field}>
                      <Input placeholder="Synonym" />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add('')} block icon={<PlusOutlined />}>
                  Add synonym
                </Button>
              </div>
            )}
          </Form.List>

          <Typography.Text strong style={{ display: 'block', marginTop: 16 }}>
            Associated barnas
          </Typography.Text>
          <Form.List name="associated_barnas">
            {(fields, { add, remove }) => (
              <div style={{ marginTop: 8 }}>
                {fields.map((field) => (
                  <AssociatedBarnaRowFields
                    key={field.key}
                    field={field}
                    form={form as unknown as FormInstance<AssociatedBarnaFormSlice>}
                    associatedBarnaOptions={associatedBarnaOptions}
                    barnaRows={barnaRows}
                    bhedRows={bhedRows}
                    onRemove={() => remove(field.name)}
                  />
                ))}
                <Button
                  type="dashed"
                  onClick={() =>
                    add({
                      barna_id: undefined,
                      included_bhedas: [],
                      bhed_filters: { ...EMPTY_BHED_FILTERS },
                    })
                  }
                  block
                  icon={<PlusOutlined />}
                  style={{ marginTop: 8 }}
                >
                  Add barna association
                </Button>
              </div>
            )}
          </Form.List>
        </Form>
      </Modal>

      <Modal
        title={detail ? detail.name : 'Pratyahar'}
        open={detailOpen}
        onCancel={() => {
          setDetailOpen(false);
          setDetail(null);
        }}
        footer={null}
        width={640}
      >
        {detailLoading ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : detail ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Typography.Text type="secondary">ID</Typography.Text>
              <div>{detail.id}</div>
            </div>
            <div>
              <Typography.Text type="secondary">Name</Typography.Text>
              <div>{detail.name}</div>
            </div>
            <div>
              <Typography.Text type="secondary">Synonyms</Typography.Text>
              <div>
                {detail.synonyms.length > 0 ? (
                  <Space wrap>
                    {detail.synonyms.map((s) => (
                      <Tag key={s}>{s}</Tag>
                    ))}
                  </Space>
                ) : (
                  '—'
                )}
              </div>
            </div>
            <div>
              <Typography.Text type="secondary">Associated barnas</Typography.Text>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                {detail.associatedBarnas.map((a) => (
                  <li key={a.barnaId} style={{ marginBottom: 8 }}>
                    <div>
                      <strong>{barnaLabel(a.barnaId, a)}</strong>
                    </div>
                    <div style={{ marginTop: 4 }}>
                      Included bhedas:{' '}
                      {a.includedBhedas.length > 0 ? (
                        <Space wrap size={[4, 4]}>
                          {a.includedBhedas.map((id) => (
                            <Tag key={id}>{bhedLabel(id)}</Tag>
                          ))}
                        </Space>
                      ) : (
                        <Typography.Text type="secondary">none</Typography.Text>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              {detail.associatedBarnas.length === 0 ? (
                <Typography.Text type="secondary">No associations</Typography.Text>
              ) : null}
            </div>
          </Space>
        ) : null}
      </Modal>
        </Card>
      </div>
    </ConfigProvider>
  );
}
